import { useEffect, useRef, useState, type ReactNode } from 'react';

export const MIN_WIDTH = 240;
export const MIN_HEIGHT = 120;

export interface CardBox {
  x: number;
  y: number;
  width: number;
  /** Null means as tall as its contents. */
  height: number | null;
}

/**
 * A dashboard card, placed like a sticky note.
 *
 * It sits wherever it was put and stays there. Dragging moves it freely
 * rather than swapping it with a neighbour, and the corner resizes it to any
 * size at all — the two things people actually mean when they say they want
 * to arrange something.
 *
 * Pointer events rather than HTML5 drag-and-drop. The drag API is built for
 * moving data between drop targets, gives no usable coordinates during the
 * drag on some browsers, and drags a translucent ghost of the element instead
 * of the element. For moving a box around a canvas, pointer events are both
 * simpler and smoother.
 */
export function WidgetFrame({
  box,
  accent,
  arranging,
  locked,
  bounds,
  onChange,
  onToggleLock,
  onHide,
  onFit,
  onRaise,
  onMeasure,
  zIndex,
  children,
}: {
  box: CardBox;
  /** The colour of the section this card belongs to. */
  accent: { wash: string; tint: string; base: string; deep: string };
  arranging: boolean;
  locked: boolean;
  /**
   * The canvas it may not leave, and how far it may grow before it would run
   * into a neighbour. Passed in rather than worked out here, because only the
   * board knows where the other cards are.
   */
  bounds: { width: number; maxWidth: number; maxHeight: number };
  onChange: (box: CardBox) => void;
  onToggleLock: () => void;
  onHide: () => void;
  onFit: () => void;
  onRaise: () => void;
  /**
   * How tall this card actually is on screen, border and all.
   *
   * Reported from here rather than from the card's contents. The contents are
   * only part of it — there is padding, a border, and while arranging a strip
   * along the top for the drag handle — and "Tidy up" was packing cards using
   * the height of the contents alone, then drawing them taller than that. The
   * difference was exactly the overlap.
   */
  onMeasure?: (height: number) => void;
  zIndex: number;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);

  /*
   * The live position during a drag.
   *
   * Held here rather than pushed up on every pointer move: the parent writes
   * the layout to localStorage whenever it changes, and doing that sixty
   * times a second while someone drags a card is wasteful. The parent is told
   * once, when the pointer comes up.
   */
  const [live, setLive] = useState<CardBox | null>(null);
  const gesture = useRef<{
    mode: 'move' | 'resize';
    startX: number;
    startY: number;
    from: CardBox;
  } | null>(null);

  const current = live ?? box;

  useEffect(() => {
    const el = ref.current;
    if (!el || !onMeasure) return;

    const report = () => onMeasure(el.offsetHeight);
    report();

    const observer = new ResizeObserver(report);
    observer.observe(el);
    return () => observer.disconnect();
  }, [onMeasure]);

  function begin(mode: 'move' | 'resize', e: React.PointerEvent) {
    if (locked || !arranging) return;

    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    onRaise();

    gesture.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      // Start from what the card actually measures, so the first pixel of
      // movement continues from where it is rather than jumping.
      from: {
        ...box,
        width: ref.current?.offsetWidth ?? box.width,
        height: mode === 'resize' ? (ref.current?.offsetHeight ?? box.height) : box.height,
      },
    };
    setLive(gesture.current.from);
  }

  function move(e: React.PointerEvent) {
    const g = gesture.current;
    if (!g) return;

    const dx = e.clientX - g.startX;
    const dy = e.clientY - g.startY;

    if (g.mode === 'move') {
      setLive({
        ...g.from,
        // Kept inside the canvas: a card dragged off the left or above the
        // top would be unreachable, and there is no scrolling up to find it.
        x: Math.min(Math.max(g.from.x + dx, 0), Math.max(0, bounds.width - g.from.width)),
        y: Math.max(g.from.y + dy, 0),
      });
    } else {
      // Bounded live, not corrected on release: a card that grows under the
      // pointer and then snaps back feels broken.
      const width = Math.min(
        Math.max(g.from.width + dx, MIN_WIDTH),
        Math.max(MIN_WIDTH, bounds.maxWidth),
      );
      const height = Math.min(
        Math.max((g.from.height ?? MIN_HEIGHT) + dy, MIN_HEIGHT),
        Math.max(MIN_HEIGHT, bounds.maxHeight),
      );
      setLive({ ...g.from, width, height });
    }
  }

  function end(e: React.PointerEvent) {
    (e.currentTarget as Element).releasePointerCapture?.(e.pointerId);
    if (live) onChange(live);
    gesture.current = null;
    setLive(null);
  }

  /** Arrow keys nudge, so a card can be placed without a steady hand. */
  function onKeyDown(e: React.KeyboardEvent) {
    if (!arranging || locked) return;

    const step = e.shiftKey ? 40 : 8;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const delta = moves[e.key];
    if (!delta) return;

    e.preventDefault();
    onChange({
      ...box,
      x: Math.min(Math.max(box.x + delta[0], 0), Math.max(0, bounds.width - box.width)),
      y: Math.max(box.y + delta[1], 0),
    });
  }

  return (
    <section
      ref={ref}
      onKeyDown={onKeyDown}
      tabIndex={arranging && !locked ? 0 : undefined}
      style={{
        position: 'absolute',
        left: current.x,
        top: current.y,
        width: current.width,
        height: current.height ?? undefined,
        zIndex: live ? 999 : zIndex,
        // The section's colour all the way round, with the faintest wash of
        // it behind — enough that a glance at the dashboard maps onto the
        // sidebar without reading a word.
        border: `2px solid ${accent.base}`,
        backgroundColor: '#FFFFFF',
        backgroundImage: `linear-gradient(${accent.wash}, ${accent.wash})`,
        touchAction: arranging && !locked ? 'none' : undefined,
      }}
      className={[
        'card flex flex-col transition-shadow',
        live ? 'shadow-lifted' : '',
        arranging && !locked ? 'ring-1 ring-clay' : '',
      ].join(' ')}
    >
      {/*
        Fixed-height cards scroll rather than spilling out of their box, and
        everything shifts down while arranging so the drag strip along the top
        does not sit over the card's own heading.
      */}
      <div
        className={[
          'min-h-0 flex-1',
          current.height ? 'overflow-auto' : '',
          arranging ? 'pt-5' : '',
        ].join(' ')}
      >
        {children}
      </div>

      {arranging && (
        <>
          {/*
            The whole strip along the top is the drag handle, rather than the
            whole card: a card is full of buttons and lists, and making all of
            it draggable means never being able to tick a to-do again.
          */}
          <div
            onPointerDown={(e) => begin('move', e)}
            onPointerMove={move}
            onPointerUp={end}
            onPointerCancel={end}
            aria-hidden
            className={[
              'absolute inset-x-0 top-0 h-7 rounded-t-[10px]',
              locked ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing',
            ].join(' ')}
            style={{ backgroundColor: accent.tint }}
          />

          <span className="pointer-events-none absolute left-2 top-1 text-[10px] text-walnut">
            {locked ? 'locked' : 'drag'}
          </span>

          <button
            onClick={onToggleLock}
            aria-pressed={locked}
            title={locked ? 'Unlock so it can be moved' : 'Lock it in place'}
            aria-label={locked ? 'Unlock this card' : 'Lock this card in place'}
            className="absolute right-14 top-0.5 rounded px-1.5 py-0.5 text-[10px] text-walnut hover:bg-cream"
          >
            {locked ? 'Unlock' : 'Lock'}
          </button>

          <button
            onClick={onFit}
            title="Back to its natural size"
            className="absolute right-8 top-0.5 rounded px-1.5 py-0.5 text-[10px] text-walnut hover:bg-cream"
          >
            Fit
          </button>

          <button
            onClick={onHide}
            title="Hide this card"
            aria-label="Hide this card"
            className="absolute right-1.5 top-0.5 rounded px-1.5 py-0.5 text-[10px] text-walnut hover:bg-cream"
          >
            Hide
          </button>

          {!locked && (
            <span
              role="slider"
              tabIndex={0}
              aria-label="Resize this card"
              aria-valuetext={`${Math.round(current.width)} pixels wide`}
              aria-valuenow={Math.round(current.width)}
              aria-valuemin={MIN_WIDTH}
              aria-valuemax={Math.round(bounds.width)}
              onPointerDown={(e) => begin('resize', e)}
              onPointerMove={move}
              onPointerUp={end}
              onPointerCancel={end}
              onKeyDown={(e) => {
                const step = e.shiftKey ? 100 : 20;
                const h = current.height ?? ref.current?.offsetHeight ?? MIN_HEIGHT;

                if (e.key === 'ArrowRight')
                  onChange({ ...box, width: Math.min(current.width + step, bounds.maxWidth) });
                if (e.key === 'ArrowLeft')
                  onChange({ ...box, width: Math.max(current.width - step, MIN_WIDTH) });
                if (e.key === 'ArrowDown')
                  onChange({ ...box, height: Math.min(h + step, bounds.maxHeight) });
                if (e.key === 'ArrowUp')
                  onChange({ ...box, height: Math.max(h - step, MIN_HEIGHT) });
              }}
              title="Drag to any size you like"
              className="absolute bottom-1 right-1 flex h-6 w-6 cursor-nwse-resize items-center justify-center rounded text-clay hover:bg-linen hover:text-terracotta"
              style={{ touchAction: 'none' }}
            >
              ◢
            </span>
          )}
        </>
      )}
    </section>
  );
}
